import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { updateConnection } from 'routes/api/routesApi';
import { AccountBundle } from '../flowMapTypes';
import {
  accountBaseUrl,
  GeneralDraft,
  generalFields,
  generalPatch,
} from './generalFields';

// The selected account's General pane (name / workspace / endpoint). Renaming a
// Connection here never touches the Route's own name — they are decoupled. The
// draft is dropped whenever the selected account changes, including changes the
// navigation guards never see (a panel requested through router state), so a
// draft left behind is never saved onto whichever account is open next.
export function useGeneralDraft({
  token,
  selectedId,
  selectedBundle,
  fallbackTechValue,
  reload,
}: {
  token: string | undefined;
  // The selected account, even while its record is still loading.
  selectedId: string | undefined;
  selectedBundle: AccountBundle | undefined;
  fallbackTechValue?: string;
  reload: (id?: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<GeneralDraft | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDraft(null);
  }, [selectedId]);

  const baseUrl = selectedBundle
    ? accountBaseUrl(selectedBundle, fallbackTechValue)
    : undefined;
  const fields = generalFields(draft, selectedBundle, baseUrl, t);

  // Resolves true when nothing about the General pane is left unsaved —
  // including when there was never a draft to begin with. Kept out of the
  // panel so the navigation guards can save this draft too.
  const save = async (): Promise<boolean> => {
    if (draft === null || !selectedBundle || !baseUrl) return true;
    if (!fields.dirty) {
      setDraft(null);
      return true;
    }
    if (fields.nameError || fields.ownerError || fields.urlError) return false;
    setSaving(true);
    try {
      const patch = generalPatch(fields, selectedBundle, baseUrl);
      // A swallowed 401 comes back as undefined — not a save.
      const saved = await updateConnection(
        token as string,
        selectedBundle.id,
        patch,
      );
      if (!saved) return false;
      toast.success(t('Connection updated'));
      setDraft(null);
      await reload(selectedBundle.id);
      return true;
    } catch (err: any) {
      console.error('failed to update connection', err);
      if (err?.response?.status !== 401) {
        toast.error(
          t('Something went wrong. Please try again or contact support.'),
        );
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const edit = (change: GeneralDraft) =>
    setDraft((prev) => ({ ...prev, ...change }));

  return {
    hasDraft: draft !== null,
    edit,
    discard: () => setDraft(null),
    save,
    saving,
    fields,
    baseUrl,
  };
}

export type GeneralDraftState = ReturnType<typeof useGeneralDraft>;
